/**
 * Pantalla de **gestión de usuarios** (`/equipo/usuarios`). Reemplaza el
 * placeholder de `ROUTES.users`. Equivale a `users.html` del SSR.
 *
 * Exige `manage_users` (mismo permiso que la API y el menú del SSR) vía
 * `RequirePermission`. Orquesta:
 *  - la lista de usuarios agrupada por sede (`useUsersGrouped`) en `UsersTable`;
 *  - alta/edición (`UserFormDialog`), cambio de contraseña (`ChangePasswordDialog`),
 *    permisos granulares (`ManagePermissionsDialog`) y borrado (`ConfirmDeleteDialog`).
 *
 * Presentacional + hooks; sin `fetch`. Estados de carga/error contemplados. La
 * gestión es global (no acotada por sede), igual que el SSR.
 */
import { useState } from "react";

import { Button, Card, CardContent } from "@/components/ui";
import { useAuth } from "@/lib/auth";

import { MANAGE_USERS_PERMISSION } from "../constants";
import { teamErrorMessage } from "../format";
import { useDeleteUser } from "../hooks/useUserMutations";
import { useUsersGrouped } from "../hooks/useUsers";
import type { UserListItem } from "../types";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { ManagePermissionsDialog } from "./ManagePermissionsDialog";
import { RequirePermission } from "./RequirePermission";
import { UserFormDialog } from "./UserFormDialog";
import { UsersTable } from "./UsersTable";

/** Objetivo del formulario de usuario (alta o edición). */
type FormTarget = { mode: "create" } | { mode: "edit"; user: UserListItem } | null;

export function UsersScreen() {
  const { user: currentUser } = useAuth();
  const usersQuery = useUsersGrouped();
  const deleteUser = useDeleteUser();

  const [formTarget, setFormTarget] = useState<FormTarget>(null);
  const [passwordTarget, setPasswordTarget] = useState<string | null>(null);
  const [permissionsTarget, setPermissionsTarget] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserListItem | null>(null);

  function handleConfirmDelete() {
    if (deleteTarget === null) {
      return;
    }
    deleteUser.mutate(deleteTarget.username, {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  /** Abre el diálogo de borrado limpiando un posible error de un intento previo. */
  function openDelete(user: UserListItem) {
    deleteUser.reset();
    setDeleteTarget(user);
  }

  return (
    <RequirePermission
      permission={MANAGE_USERS_PERMISSION}
      title="Usuarios"
      deniedMessage="No tienes permiso para gestionar usuarios."
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">Usuarios</h1>
          <Button
            type="button"
            variant="primary"
            onClick={() => setFormTarget({ mode: "create" })}
          >
            Nuevo usuario
          </Button>
        </div>

        {usersQuery.isPending ? (
          <Card>
            <CardContent>
              <p className="py-8 text-center text-sm text-slate-500" role="status">
                Cargando usuarios…
              </p>
            </CardContent>
          </Card>
        ) : usersQuery.isError ? (
          <Card>
            <CardContent>
              <p className="py-8 text-center text-sm text-red-600" role="alert">
                {teamErrorMessage(usersQuery.error)}
              </p>
            </CardContent>
          </Card>
        ) : (
          <UsersTable
            groups={usersQuery.data.groups}
            currentUsername={currentUser?.username ?? null}
            onEdit={(user) => setFormTarget({ mode: "edit", user })}
            onChangePassword={(username) => setPasswordTarget(username)}
            onManagePermissions={(username) => setPermissionsTarget(username)}
            onDelete={openDelete}
          />
        )}
      </div>

      <UserFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
      <ChangePasswordDialog
        username={passwordTarget}
        onClose={() => setPasswordTarget(null)}
      />
      <ManagePermissionsDialog
        username={permissionsTarget}
        onClose={() => setPermissionsTarget(null)}
      />
      <ConfirmDeleteDialog
        open={deleteTarget !== null}
        title="Eliminar usuario"
        description={
          deleteTarget
            ? `¿Eliminar al usuario "${deleteTarget.username}"? Esta acción no se puede deshacer.`
            : ""
        }
        errorMessage={deleteUser.isError ? teamErrorMessage(deleteUser.error) : null}
        isPending={deleteUser.isPending}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </RequirePermission>
  );
}
